import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
    MvsFormFieldOutputBaseComponent
} from 'features/core/shared/form/field/output/mvs-form-field-output-base.component';
import {DtoEnumInfo} from 'features/core/shared/dto/dto-enum-info.interface';

/**
 * Output field component for the ORIGIN of a resolved provision value — "eigene Zeile" or
 * "geerbt aus «Satzbild mtx047»".
 *
 * ── WHY THIS IS A COMPONENT AND NOT INLINE HTML (design gate auflage H5)
 *
 * A factor without the matrix it came from is worthless on the Beratersicht: 127 of the 128
 * matrices of the reference stock inherit, so "where does this number come from" is the normal
 * question, not the exception. That makes the origin chip a piece of display VOCABULARY, not
 * decoration of one table — the same chip belongs on the ProvisionType matrix panes the moment
 * anyone renders a resolved row there. The client signed the decision as "werden anderswo wieder
 * gebraucht; der native Weg ist hier auch der billigere".
 *
 * Extends {@link MvsFormFieldOutputBaseComponent}, so `value` / `formField` / `form` / `dto` mean
 * what they mean everywhere else and the class is usable wherever an output field is.
 *
 * ── HOW IT IS REACHED
 *
 * By SELECTOR, the documented direct-usage path of `fe_core_outputs`:
 *
 * ```html
 * <cp-form-field-output-origin [value]="row.origin"
 *                              [enumInfo]="row.originInfo"
 *                              [matrixName]="row.originMatrixName">
 * </cp-form-field-output-origin>
 * ```
 *
 * NOT through an `ADDITIONAL_OUTPUT_FIELDS` provider in `CpModule`, and that is a measured
 * decision, not an omission. `ADDITIONAL_OUTPUT_FIELDS` is a NON-multi `InjectionToken`
 * (`shared/form/field/service/additional-output-fields.ts`) that `CcModule` already provides for
 * its `struct` / `array` / `list` controls; its only consumer, `MvsFormFieldService`, is
 * `providedIn: 'root'` and therefore always reads the ROOT injector's map. A second provider in the
 * lazily loaded `CpModule` would consequently (a) never be seen by the resolving service and
 * (b) shadow cc's map for anything injecting the token inside cp's scope — a silent break of
 * foreign output controls in exchange for nothing. Its sibling token
 * `ADDITIONAL_INPUT_FIELDS` IS declared `multi: true` and could be extended safely; the output one
 * cannot. Should the token ever become `multi`, registering under the key `provisionOrigin` is a
 * one-line addition and nothing in this class changes.
 *
 * ── THE INHERITANCE PATH IS NOT HERE
 *
 * The chip is the overview; the full K-7 chain (up to four levels) belongs behind a click, in the
 * row expansion of the hosting table (FD-4). The chain arrives from the backend already SPLIT into
 * steps (`matrixInheritancePath: string[]`) — nothing in the frontend ever splits a path string,
 * which would be re-implementing the chain walk in the browser.
 */
@Component({
    selector: 'cp-form-field-output-origin',
    templateUrl: './cp-form-field-output-origin.component.html',
    styleUrls: ['./cp-form-field-output-origin.component.scss'],
    standalone: false
})
export class CpFormFieldOutputOriginComponent
    extends MvsFormFieldOutputBaseComponent
    implements OnInit, OnDestroy, OnChanges {

    /**
     * The backend's `originInfo` block — German label, icon and colours straight from the
     * `@UiEnumValueInfo` on `ProvisionConfigurationOrigin`.
     *
     * Preferred over the local mapping below, and the reason a constant appended to the backend
     * enum after this file was written still renders with a proper label instead of a raw name.
     */
    @Input() enumInfo: DtoEnumInfo;

    /**
     * Name of the matrix the row physically stands in (`originMatrixName`) — the "warum" half of
     * the chip. Absent only when the backend could not name it.
     */
    @Input() matrixName: string;

    /** The rendered chip text. */
    label: string;

    /** Font Awesome icon of the chip. */
    icon: string;

    /** Tooltip — always the long form, also when the chip itself had to be shortened. */
    tooltip: string;

    /** True for an inherited row; drives the chip's colour only. */
    inherited: boolean;

    override ngOnInit() {
        super.ngOnInit();
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }
        this.refreshComponent();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
    }

    /**
     * Builds the chip text.
     *
     * A NULL origin is a real statement and gets its own text ("kein Wert", requirement §2.2): the
     * assignment carries no row at all. It must never fall through to "eigene Zeile", which would
     * claim a value exists.
     */
    override refreshComponent(): void {

        this.inherited = this.isInherited();

        if (this.value == null) {
            this.label = 'kein Wert';
            this.icon = 'fa-regular fa-circle-minus';
            this.tooltip = 'Für diese Zuordnung wurde keine Matrixzeile gefunden';
            return;
        }

        if (this.inherited) {
            this.label = this.matrixName ? 'geerbt aus ' + this.matrixName : 'geerbt';
            this.icon = this.enumInfo?.image || 'fa-solid fa-diagram-project';
            this.tooltip = this.matrixName
                ? 'Die Zeile stammt aus der übergeordneten Matrix „' + this.matrixName
                + '“ — nicht aus der zugeordneten Matrix selbst.'
                : 'Die Zeile stammt aus einer übergeordneten Matrix der Vererbungskette.';
            return;
        }

        this.label = this.enumInfo?.label || 'eigene Zeile';
        this.icon = this.enumInfo?.image || 'fa-solid fa-table-cells';
        this.tooltip = this.matrixName
            ? 'Die Zeile steht in der zugeordneten Matrix „' + this.matrixName + '“ selbst.'
            : 'Die Zeile steht in der zugeordneten Matrix selbst.';
    }

    /**
     * Whether the value denotes an inherited row.
     *
     * Accepts BOTH wire forms on purpose, the convention this module's API DTOs already carry
     * ("serialized as the enum name, ordinal tolerated"): Jackson sends the enum NAME, while the
     * position-based cp enum mirrors make an ordinal a legitimate second form. An unknown value is
     * treated as NOT inherited — the honest reading, because the only other constant today is OWN.
     */
    protected isInherited(): boolean {

        if (typeof this.value === 'string') {
            return this.value === 'INHERITED';
        }

        // Ordinal 1 == INHERITED in ProvisionConfigurationOrigin; the ordinals are frozen there.
        return this.value === 1;
    }

}
